import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircleHeart, X, Send, Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

const SUGGESTIONS = [
  "What should I gift my sister?",
  "Birthday gift under ₹699?",
  "Something cute for my girlfriend",
];

const cleanMd = (t = "") =>
  t.replace(/\*\*(.*?)\*\*/g, "$1").replace(/\*(.*?)\*/g, "$1").replace(/^#+\s*/gm, "").replace(/^\s*[-*]\s+/gm, "• ");

export const ConciergeWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi there! 💝 Tell me who you're gifting and your budget, and I'll help you find the perfect parcel." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const scrollRef = useRef(null);

  // Never cover cart/checkout/modal UI: collapse away while any drawer or dialog is open.
  useEffect(() => {
    const check = () => {
      const dialog = document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]');
      const locked = document.body.hasAttribute("data-scroll-locked");
      setOverlayOpen(Boolean(dialog || locked));
    };
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.body, { attributes: true, childList: true, subtree: true });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("wp-open-concierge", openHandler);
    return () => window.removeEventListener("wp-open-concierge", openHandler);
  }, []);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    const history = messages.map((m) => ({ role: m.role, text: m.text }));
    setMessages((prev) => [...prev, { role: "user", text: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/concierge", { message: msg, history });
      setMessages((prev) => [...prev, { role: "assistant", text: data.reply }]);
    } catch {
      setMessages((prev) => [...prev, { role: "assistant", text: "Sorry, I had a little hiccup. Could you try again?" }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!(overlayOpen && !open) && (
      <motion.button
        onClick={() => setOpen((v) => !v)}
        data-testid="ai-concierge-open-button"
        aria-label="Need help choosing?"
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.96 }}
        className={`fixed bottom-24 right-4 z-[60] flex h-12 items-center gap-2 rounded-full bg-wp-ocean text-white shadow-wp-lift md:right-6 ${open ? "w-12 justify-center" : "w-12 justify-center lg:w-auto lg:px-5"}`}
      >
        {open ? <X className="h-6 w-6" /> : (
          <>
            <MessageCircleHeart className="h-6 w-6" />
            <span className="hidden pr-0.5 text-sm font-semibold lg:inline">Need help choosing?</span>
          </>
        )}
      </motion.button>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
            className="fixed bottom-40 right-4 z-[60] flex h-[28rem] w-[min(92vw,22rem)] flex-col overflow-hidden rounded-wp-xl border border-border bg-card shadow-wp-lift md:right-6"
            data-testid="ai-concierge-panel"
          >
            <div className="wp-mint-gradient flex items-center gap-2 border-b border-border px-4 py-3">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-white text-wp-ocean shadow"><Sparkles className="h-5 w-5" /></span>
              <div>
                <p className="text-sm font-semibold">Need help choosing?</p>
                <p className="text-[11px] text-wp-ink-muted">Tell us who it's for — we'll do the rest</p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-3 py-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[82%] whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${m.role === "user" ? "bg-wp-rose-strong text-white" : "bg-wp-blush-100 text-wp-ink"}`}>
                    {m.role === "assistant" ? cleanMd(m.text) : m.text}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="flex items-center gap-1.5 rounded-2xl bg-wp-blush-100 px-3.5 py-2.5 text-wp-ink-muted">
                    <Loader2 className="h-4 w-4 animate-spin" /> thinking…
                  </div>
                </div>
              )}
              {messages.length <= 1 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)} className="wp-chip hover:bg-wp-blush-200">{s}</button>
                  ))}
                </div>
              )}
            </div>

            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-border p-2.5">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask me anything about gifting…"
                data-testid="ai-concierge-message-input"
                className="flex-1 rounded-full border border-border bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-wp-ocean/40"
              />
              <button type="submit" disabled={loading} aria-label="Send" data-testid="ai-concierge-send-button"
                className="grid h-10 w-10 place-items-center rounded-full bg-wp-ocean text-white disabled:opacity-50">
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
